import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Phone, Mail, Play } from 'lucide-react';
import SEO from '../components/SEO';

const InformationalVideos = () => {
  const [selectedVideo, setSelectedVideo] = React.useState(null);
  
  const videos = [
    {
      title: 'Your Guide to the Medicare Enrollment Periods',
      description: 'Learn about Initial Enrollment, Annual Enrollment, and Special Enrollment Periods.',
      videoId: 'V6fTOgCk9iM',
      category: 'Enrollment'
    },
    {
      title: 'Get Started: Parts of Medicare',
      description: 'Understanding Medicare Parts A, B, C, and D - what each covers.',
      videoId: 'MlNw4LovNjE',
      category: 'Medicare Basics'
    },
    {
      title: 'Medicare & You: Traveling Abroad',
      description: 'What you need to know about Medicare coverage when traveling outside the US.',
      videoId: 'P2ji8yf8vFU',
      category: 'Travel Coverage'
    },
    {
      title: 'Medicare\'s Preventive Benefits',
      description: 'Free preventive services covered by Medicare to keep you healthy.',
      videoId: '3ljyLlaDUEU',
      category: 'Preventive Care'
    },
    {
      title: 'Look Out for Enrollment Fraud',
      description: 'Protect yourself from Medicare enrollment scams and fraudulent practices.',
      videoId: 'iByQfda5PMo',
      category: 'Fraud Prevention'
    },
    {
      title: 'Annual Wellness Visits (AWV) Video',
      description: 'Learn about your free annual wellness visit and what to expect.',
      videoId: 'r7yOUaMJyJU',
      category: 'Wellness'
    }
  ];

  const handleVideoClick = (video) => {
    setSelectedVideo(video);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <div className="informational-videos-page">
      <SEO 
        title="Informational Videos"
        description="Educational Medicare and health insurance videos. Learn about enrollment periods, plan types, coverage options, and more through our comprehensive video library."
        keywords="Medicare videos, health insurance education, Medicare enrollment videos, Medicare guide, health insurance tutorials"
        path="/informational-videos"
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-teal-50 to-blue-50 py-16 px-4">
        <div className="container mx-auto max-w-7xl text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Informational Videos</h1>
          <p className="text-xl text-gray-700 mb-6">
            Educational resources to help you understand Medicare and health insurance
          </p>
        </div>
      </section>

      {/* Video Player Section */}
      {selectedVideo && (
        <section className="py-8 px-4 bg-gray-50">
          <div className="container mx-auto max-w-5xl">
            <Card className="border-2 border-teal-200">
              <CardContent className="p-4 md:p-6">
                <div className="aspect-video bg-black rounded-lg overflow-hidden mb-4">
                  <iframe
                    width="100%"
                    height="100%"
                    src={`https://www.youtube.com/embed/${selectedVideo.videoId}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
                    title={selectedVideo.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    className="w-full h-full"
                  ></iframe>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">{selectedVideo.title}</h3>
                <p className="text-gray-600 mb-4">{selectedVideo.description}</p>
                <Button 
                  variant="outline" 
                  onClick={() => setSelectedVideo(null)}
                  className="border-teal-600 text-teal-600 hover:bg-teal-50"
                >
                  Close Video
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* Video Grid */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-7xl">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">Browse Our Video Library</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {videos.map((video, index) => (
              <Card 
                key={index} 
                className="border-2 border-teal-200 hover:shadow-xl transition-all cursor-pointer group overflow-hidden"
                onClick={() => handleVideoClick(video)}
              >
                <CardContent className="p-0">
                  <div className="relative aspect-video overflow-hidden">
                    <img 
                      src={`https://img.youtube.com/vi/${video.videoId}/hqdefault.jpg`}
                      alt={video.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
                      <div className="w-16 h-16 bg-teal-600 bg-opacity-90 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                        <Play className="w-8 h-8 text-white ml-1" fill="white" />
                      </div>
                    </div>
                    <div className="absolute top-2 left-2 bg-teal-600 text-white text-xs px-3 py-1 rounded-full font-medium shadow-md">
                      {video.category}
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors line-clamp-2">
                      {video.title}
                    </h3>
                    <p className="text-gray-600 text-sm line-clamp-2">{video.description}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Topics Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">Video Topics</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="border-2 border-teal-200">
              <CardContent className="p-6 text-center">
                <h3 className="text-lg font-semibold text-teal-600 mb-2">Medicare Basics</h3>
                <p className="text-gray-600 text-sm">Understanding Parts A, B, C & D</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-teal-200">
              <CardContent className="p-6 text-center">
                <h3 className="text-lg font-semibold text-teal-600 mb-2">Enrollment</h3>
                <p className="text-gray-600 text-sm">When and how to sign up</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-teal-200">
              <CardContent className="p-6 text-center">
                <h3 className="text-lg font-semibold text-teal-600 mb-2">Plan Comparison</h3>
                <p className="text-gray-600 text-sm">Choosing the right coverage</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-teal-200">
              <CardContent className="p-6 text-center">
                <h3 className="text-lg font-semibold text-teal-600 mb-2">Fraud Prevention</h3>
                <p className="text-gray-600 text-sm">Protecting yourself from scams</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="py-16 px-4 bg-gradient-to-br from-teal-50 to-blue-50">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl font-bold mb-4 text-gray-900">Still Have Questions?</h2>
          <p className="text-xl text-gray-700 mb-8">
            Our team is here to provide personalized guidance
          </p>
          <div className="space-y-4">
            <Button asChild size="lg" className="bg-teal-600 hover:bg-teal-700 text-white text-xl px-8 py-6">
              <a href="tel:734.209.6712">
                <Phone className="mr-2" />
                Call: 734.209.6712
              </a>
            </Button>
            <div className="flex items-center justify-center gap-2 text-gray-700">
              <Mail size={20} />
              <span>Email 24/7: <a href="mailto:Paulette@llplans.com" className="text-teal-600 hover:text-teal-700">Paulette@llplans.com</a></span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InformationalVideos;